import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  BookOpen, ArrowLeft, QrCode, Users, Edit2, Plus, 
  Copy, Check, ExternalLink, Download
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = window.location.origin;

const ClassManagementPage = ({ user }) => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingClass, setEditingClass] = useState(null);
  const [newName, setNewName] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addClassName, setAddClassName] = useState("");
  const [addDisplayName, setAddDisplayName] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [selectedQR, setSelectedQR] = useState(null);

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`);
      setClasses(response.data);
    } catch (error) {
      console.error("Error fetching classes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (cls) => {
    setEditingClass(cls);
    setNewName(cls.name);
    setNewDisplayName(cls.display_name || cls.name);
  };

  const handleSaveEdit = async () => {
    if (!newName.trim()) {
      toast.error("Le nom ne peut pas être vide");
      return;
    }

    try {
      await axios.put(`${API}/classes/${editingClass.id}`, null, {
        params: { name: newName, display_name: newDisplayName }
      });
      toast.success("Classe mise à jour !");
      setEditingClass(null);
      fetchClasses();
    } catch (error) {
      console.error("Error updating class:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleAddClass = async () => {
    if (!addClassName.trim()) {
      toast.error("Le nom ne peut pas être vide");
      return;
    }

    try {
      await axios.post(`${API}/classes`, null, {
        params: { name: addClassName, display_name: addDisplayName || addClassName }
      });
      toast.success("Classe créée !");
      setShowAddDialog(false);
      setAddClassName("");
      setAddDisplayName("");
      fetchClasses();
    } catch (error) {
      console.error("Error creating class:", error);
      toast.error("Erreur lors de la création");
    }
  };

  const copyLink = (className) => {
    const link = `${BASE_URL}/join/${encodeURIComponent(className)}`;
    navigator.clipboard.writeText(link);
    setCopiedId(className);
    toast.success("Lien copié !");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const downloadQR = (classId, className) => {
    const svg = document.getElementById(`qr-${classId}`);
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    
    img.onload = () => {
      canvas.width = 400;
      canvas.height = 400;
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, 400, 400);
      ctx.drawImage(img, 0, 0, 400, 400);
      
      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.download = `QR-${className}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    toast.success("QR Code téléchargé !");
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="class-management-page">
      {/* Navigation */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/director")}
            className="text-[#1A365D]"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <span className="navbar-logo hidden sm:block">Gestion des Classes</span>
        </div>
        
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4AF37] hover:bg-[#B3932E] text-[#0F172A]">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une classe
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouvelle classe</DialogTitle>
              <DialogDescription>Créer une nouvelle classe de Terminale</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label>Code de la classe</Label>
                <Input
                  value={addClassName}
                  onChange={(e) => setAddClassName(e.target.value.toUpperCase())}
                  placeholder="Ex: T04"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Nom complet (optionnel)</Label>
                <Input
                  value={addDisplayName}
                  onChange={(e) => setAddDisplayName(e.target.value)}
                  placeholder="Ex: Terminale 04 - Philosophie"
                  className="mt-1"
                />
              </div>
              <Button onClick={handleAddClass} className="w-full bg-[#1A365D]">
                Créer la classe
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </nav>

      <main className="px-4 py-6 max-w-5xl mx-auto page-enter">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Classes & QR Codes
          </h1>
          <p className="text-[#64748B]">
            Gérez les classes et générez les QR codes d'accès pour les élèves
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#64748B]">Chargement...</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {classes.map((cls) => (
              <Card key={cls.class_name} className="card-sjh" data-testid={`class-card-${cls.class_name}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {cls.class_name}
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(cls)}
                      className="text-[#64748B] hover:text-[#1A365D]"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <CardDescription className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {cls.student_count} élève{cls.student_count > 1 ? 's' : ''}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Class name banner above QR */}
                  <div className="bg-[#1A365D] text-white text-center py-2 rounded-t-lg font-semibold">
                    {cls.class_name}
                  </div>
                  
                  {/* QR Code */}
                  <div 
                    className="bg-white p-4 rounded-b-lg border border-t-0 border-[#E2E8F0] mb-4 flex flex-col items-center cursor-pointer hover:border-[#1A365D] transition-colors"
                    onClick={() => setSelectedQR(cls)}
                  >
                    <QRCodeSVG
                      id={`qr-${cls.class_name}`}
                      value={`${BASE_URL}/join/${encodeURIComponent(cls.class_name)}`}
                      size={160}
                      level="H"
                      includeMargin={true}
                      fgColor="#1A365D"
                    />
                    <p className="text-xs text-[#64748B] mt-2">Cliquez pour agrandir</p>
                  </div>
                  
                  {/* Stats */}
                  <div className="flex items-center justify-center mb-4">
                    <Badge className="bg-[#D4AF37] text-[#0F172A]">
                      <Users className="w-3 h-3 mr-1" />
                      {cls.student_count} élève{cls.student_count > 1 ? 's' : ''} inscrit{cls.student_count > 1 ? 's' : ''}
                    </Badge>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyLink(cls.class_name)}
                      className="flex-1 border-[#1A365D] text-[#1A365D]"
                    >
                      {copiedId === cls.class_name ? (
                        <Check className="w-4 h-4 mr-1" />
                      ) : (
                        <Copy className="w-4 h-4 mr-1" />
                      )}
                      Copier le lien
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadQR(cls.class_name, cls.class_name)}
                      className="border-[#D4AF37] text-[#D4AF37]"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={!!editingClass} onOpenChange={() => setEditingClass(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Modifier la classe</DialogTitle>
              <DialogDescription>Modifier le nom de la classe {editingClass?.class_name}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label>Code de la classe</Label>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value.toUpperCase())}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Nom complet</Label>
                <Input
                  value={newDisplayName}
                  onChange={(e) => setNewDisplayName(e.target.value)}
                  className="mt-1"
                />
              </div>
              <Button onClick={handleSaveEdit} className="w-full bg-[#1A365D]">
                Enregistrer
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* QR Code Preview Dialog - Full Screen for Projection */}
        <Dialog open={!!selectedQR} onOpenChange={() => setSelectedQR(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-center text-2xl text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
                {selectedQR?.class_name}
              </DialogTitle>
              <DialogDescription className="text-center text-lg">
                Scannez ce QR code pour rejoindre la classe
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center py-6">
              {/* Large class name banner */}
              <div className="bg-[#1A365D] text-white text-center py-3 px-8 rounded-t-xl w-full max-w-sm">
                <p className="text-2xl font-bold">{selectedQR?.class_name}</p>
              </div>
              
              <div className="bg-white p-8 rounded-b-xl border-2 border-t-0 border-[#1A365D] w-full max-w-sm flex justify-center">
                <QRCodeSVG
                  value={`${BASE_URL}/join/${encodeURIComponent(selectedQR?.class_name || '')}`}
                  size={300}
                  level="H"
                  includeMargin={true}
                  fgColor="#1A365D"
                />
              </div>
              
              <p className="mt-4 text-sm text-[#64748B] text-center">
                {BASE_URL}/join/{encodeURIComponent(selectedQR?.class_name || '')}
              </p>
              
              <div className="flex gap-2 mt-4">
                <Button
                  onClick={() => copyLink(selectedQR?.class_name)}
                  className="bg-[#1A365D]"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copier le lien
                </Button>
                <Button
                  variant="outline"
                  onClick={() => downloadQR(selectedQR?.class_name, selectedQR?.class_name)}
                  className="border-[#D4AF37] text-[#D4AF37]"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default ClassManagementPage;
